import { Link } from "react-router-dom";

/**
 * JobClose AI — Logo
 *
 * The SOURCE OF TRUTH for the JobClose AI logo is the approved asset at
 * /app/frontend/public/brand/jobclose-ai-approved-logo-full.png
 *
 * This component NEVER recreates or approximates the logo. It always
 * renders one of these two approved image files:
 *   • jobclose-ai-approved-logo-full.png  — mark + wordmark + tagline
 *   • jobclose-ai-approved-logo-mark.png  — hexagon mark only (transparent)
 *
 * The approved wordmark is dark text on light. On DARK surfaces we wrap
 * the same approved asset in a subtle light "brand plaque" backing so the
 * approved treatment is preserved verbatim — no color remapping, no font
 * substitution, no recreation.
 *
 * Variants:
 *   full     → mark + wordmark + tagline (default)
 *   mark     → hexagon mark only
 *
 * Themes:
 *   dark     → for LIGHT surfaces (default, renders directly)
 *   light    → for DARK surfaces (renders inside a subtle light plaque)
 *   badge    → forces the light-plaque wrapper even on light surfaces
 */

const FULL_SRC = "/brand/jobclose-ai-approved-logo-full.png";
const MARK_SRC = "/brand/jobclose-ai-approved-logo-mark.png";

const HEIGHTS = {
  xs: 32,
  sm: 44,
  md: 56,
  lg: 80,
  xl: 120,
  "2xl": 160,
};

const resolveHeight = (val) => {
  if (typeof val === "number") return val;
  return HEIGHTS[val] || HEIGHTS.md;
};

const Mark = ({ size = 40, className = "" }) => (
  <img
    src={MARK_SRC}
    alt="JobClose AI"
    height={size}
    style={{ height: size, width: "auto" }}
    className={`shrink-0 select-none ${className}`}
    draggable={false}
  />
);

const FullLogo = ({ height = 48, className = "" }) => (
  <img
    src={FULL_SRC}
    alt="JobClose AI — Built by a contractor, for contractors."
    height={height}
    style={{ height, width: "auto" }}
    className={`shrink-0 select-none ${className}`}
    draggable={false}
  />
);

const Plaque = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-xl bg-white px-3 py-2 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.35)] border border-white/60 ${className}`}
  >
    {children}
  </span>
);

const Logo = ({
  variant = "full",
  theme = "dark",         // "dark" = on light bg | "light" = on dark bg | "badge" = force plaque
  to = "/",
  size = "md",            // xs / sm / md / lg / xl / 2xl OR number
  className = "",
  "data-testid": testId = "brand-logo",
  // Legacy props kept for backward-compatibility with existing call sites.
  // Ignored: showTagline, taglineText, wordmarkSize, gap
  // (The tagline is baked into the approved full logo asset.)
  ...restLegacy
}) => {
  const h = resolveHeight(size);
  const needsPlaque = theme === "light" || theme === "badge";

  let inner;
  if (variant === "mark" || variant === "favicon") {
    inner = <Mark size={h} />;
  } else {
    inner = <FullLogo height={h} />;
  }

  if (needsPlaque && variant !== "mark") {
    // On dark surfaces we wrap the approved full logo in a light plaque
    // so the approved dark-on-light wordmark treatment is preserved verbatim.
    inner = <Plaque>{inner}</Plaque>;
  }

  if (!to) {
    return (
      <div className={`inline-flex ${className}`} data-testid={testId}>
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={`inline-flex items-center ${className}`}
      data-testid={testId}
      aria-label="JobClose AI"
    >
      {inner}
    </Link>
  );
};

export default Logo;
export { Mark, FullLogo };
